import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { Book, BookContextValue, ImageCreditsResponse } from '../types';
import { useAuth } from './AuthContext';
import { authenticatedGet } from '../utils/api';

const BookContext = createContext<BookContextValue | undefined>(undefined);

const getBookDetailsEndpoint = process.env.REACT_APP_LAMBDA_GET_USERS_BOOKS;
const getImageCreditsEndpoint = process.env.REACT_APP_LAMBDA_GET_USER_CREDITS;

interface BookProviderProps {
  children: ReactNode;
}

export const BookProvider: React.FC<BookProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [lastUploadedBook, setLastUploadedBook] = useState<Book | null>(null);

  const [availableImages, setAvailableImages] = useState<number | null>(null);
  const [isLoadingCredits, setIsLoadingCredits] = useState<boolean>(true);
  const [errorCredits, setErrorCredits] = useState<string | null>(null);
  
  // Use ref to track current book ID to avoid dependency issues
  const selectedBookIdRef = React.useRef<string | null>(null);

  const fetchImageCredits = useCallback(async (): Promise<void> => {
    if (!isAuthenticated || !user?.userId) {
      setAvailableImages(null);
      setIsLoadingCredits(false);
      setErrorCredits('User not authenticated');
      return;
    }

    setIsLoadingCredits(true);
    setErrorCredits(null);

    if (!getImageCreditsEndpoint) {
      console.error('getImageCreditsEndpoint is not defined. Check .env file.');
      setErrorCredits('API endpoint for credits not configured.');
      setAvailableImages(0);
      setIsLoadingCredits(false);
      return;
    }

    try {
      const response = await authenticatedGet<ImageCreditsResponse>(getImageCreditsEndpoint);
      setAvailableImages(response.data.availableImages);
    } catch (error) {
      console.error('Error fetching image credits in BookContext:', error);
      if (axios.isAxiosError(error) && error.response && error.response.status === 404) {
        setAvailableImages(0);
        setErrorCredits('New user or credits not initialized.');
      } else {
        setErrorCredits('Could not retrieve image credits.');
        setAvailableImages(null);
      }
    }
    setIsLoadingCredits(false);
  }, [isAuthenticated, user?.userId]);

  useEffect(() => {
    fetchImageCredits();
  }, [fetchImageCredits]);

  useEffect(() => {
    let debounceTimeout: NodeJS.Timeout;
    
    const handleFocus = () => {
      console.log('Window focused, scheduling credit fetch.');
      // Clear any existing timeout
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
      // Debounce the credit fetch by 1 second to prevent rapid successive calls
      debounceTimeout = setTimeout(() => {
        console.log('Executing debounced credit fetch.');
        fetchImageCredits();
      }, 1000);
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
    };
  }, [fetchImageCredits]);

  // Keep ref in sync with selectedBook
  useEffect(() => {
    selectedBookIdRef.current = selectedBook?.FileId || null;
  }, [selectedBook]);

  const refreshSelectedBook = useCallback(async (): Promise<Book | null> => {
    const bookId = selectedBookIdRef.current;
    
    if (!bookId || isRefreshing || !isAuthenticated || !user?.userId) return null;
    
    setIsRefreshing(true);
    try {
      // Pass FileId to backend to get only this specific book
      const params = new URLSearchParams({
        FileId: bookId
      });

      const url = `${getBookDetailsEndpoint}?${params.toString()}`;
      
      const response = await authenticatedGet<{books: Book[]}>(url);
      
      if (response.data?.books && response.data.books.length > 0) {
        // Should only return one book when FileId is specified
        const updatedBook = response.data.books[0];
        
        if (updatedBook && updatedBook.FileId === bookId) {
          // Update the selectedBook with fresh data
          setSelectedBook(prevBook => {
            // Only log status changes
            if (prevBook && updatedBook.ProcessingStatus !== prevBook.ProcessingStatus) {
              console.log(`Status update: ${updatedBook.ProcessingStatus}`);
            }
            return updatedBook;
          });
          return updatedBook;
        } else {
          console.warn(`Returned book doesn't match requested FileId: ${bookId}`);
          return null;
        }
      } else {
        console.warn(`No book found for FileId: ${bookId}`);
      }
      return null;
    } catch (error) {
      console.error(`Error refreshing book details for ${bookId}:`, error);
      if (axios.isAxiosError(error)) {
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
      }
      return null;
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, isAuthenticated, user?.userId]);

  const contextValue: BookContextValue = {
    selectedBook,
    setSelectedBook,
    refreshTrigger,
    setRefreshTrigger,
    refreshSelectedBook,
    isRefreshing,
    lastUploadedBook,
    setLastUploadedBook,
    availableImages,
    isLoadingCredits,
    errorCredits,
    fetchImageCredits
  };

  return (
    <BookContext.Provider value={contextValue}>
      {children}
    </BookContext.Provider>
  );
};

export const useBook = (): BookContextValue => {
  const context = useContext(BookContext);
  if (context === undefined) {
    throw new Error('useBook must be used within a BookProvider');
  }
  return context;
}; 